// ============================================================================
// BOT CONTROLLER FACTORY - Creates appropriate bot controllers based on mode
// ============================================================================

import { Player } from '@/game/entities/Player';
import { IBotController, BotAIMode } from './IBotController';
import { RuleBasedBotController, BotConfig } from './BotAI';
import { SLMBotController } from './SLMBotController';

/**
 * Factory for creating bot controllers
 * Allows runtime switching between AI modes
 */
export class BotControllerFactory {
  private static currentMode: BotAIMode = BotAIMode.RuleBased;
  private static slmAvailable: boolean = false;
  
  /**
   * Create a bot controller for the given player
   */
  static create(player: Player, config?: Partial<BotConfig>): IBotController {
    if (this.currentMode === BotAIMode.SLM && this.slmAvailable) {
      return new SLMBotController(player);
    }
    // Default to rule-based
    if (this.currentMode === BotAIMode.SLM) {
      console.warn('[BotControllerFactory] SLM not available, falling back to rule-based');
    }
    return new RuleBasedBotController(player, config);
  }
  
  /**
   * Get current AI mode
   */
  static getMode(): BotAIMode {
    return this.currentMode;
  }
  
  /**
   * Set AI mode for new bot creation
   * Existing bots keep their current controller
   */
  static setMode(mode: BotAIMode): void {
    console.log(`[BotControllerFactory] AI mode set to: ${mode}`);
    this.currentMode = mode;
  }
  
  /**
   * Check if SLM is available
   */
  static isSLMAvailable(): boolean {
    return this.slmAvailable;
  }
  
  /**
   * Mark SLM as available (called when WebLLM is ready)
   */
  static setSLMAvailable(available: boolean): void {
    this.slmAvailable = available;
    console.log(`[BotControllerFactory] SLM availability: ${available}`);
  }
  
  /**
   * Get human-readable name for current mode
   */
  static getModeName(): string {
    switch (this.currentMode) {
      case BotAIMode.SLM:
        return 'SLM (AI Language Model)';
      case BotAIMode.RuleBased:
      default:
        return 'Rule-Based (Classic)';
    }
  }
  
  /**
   * Cycle to next available AI mode
   */
  static cycleMode(): BotAIMode {
    if (this.currentMode === BotAIMode.RuleBased && this.slmAvailable) {
      this.currentMode = BotAIMode.SLM;
    } else {
      this.currentMode = BotAIMode.RuleBased;
    }
    console.log(`[BotControllerFactory] Cycled to: ${this.getModeName()}`);
    return this.currentMode;
  }
}

// Re-export types for convenience
export type { IBotController, BotGameContext, BotStateInfo } from './IBotController';
export { BotAIMode, TacticalIntent } from './IBotController';
export type { BotConfig } from './BotAI';
export { RuleBasedBotController, BotAI, BotState } from './BotAI';
export { SLMBotController } from './SLMBotController';
